import type { Logger } from '@mimir/common/types/logger';
import type { VideoDecoder, VideoPacket, DecodedVideoFrame } from './demuxAndDecodeFrameProvider';
import type { TestPacket } from './testPacket';
import { decodeTestPacket } from './testPacket';
import { approximateFractionValue, compareFractions, divideFractions, formatFractionAsExpression, multiplyFractions } from '@mimir/common/util/integerMath';
import type { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import type { Fraction } from '@mimir/common/util/fractionType';

export interface TestVideoDecoderSettings {
  /**
   *  Set to true in order to emit the decoded frame in decode order. This is
   *  normal "sane" and expected behavior for a video decoder, but some
   *  implementations (Apple media stack for instance) may return video frames
   *  not in presentation order, but rather in decode order. So by setting
   *  this to false we can emulate this weird behavior.
   */
  emitDecodedFramesInPresentationOrder: boolean,
}

export function createTestVideoDecoder(
  logger: Logger,
  bufferCache: BufferCache,
  settings: TestVideoDecoderSettings,
): VideoDecoder {
  return createTestVideoDecoderInternal(logger, bufferCache, settings);
}

// ----
//
export function createTestVideoDecoderInternal(
  logger: Logger,
  bufferCache: BufferCache,
  settings: TestVideoDecoderSettings,
): VideoDecoder {
  const pendingPackets : VideoPacket[] = [];
  let flushed = false;

  const { emitDecodedFramesInPresentationOrder } = settings;

  let lastFrameNumber : number | null = null;
  let lastDtsPushed : Fraction | null = null;

  const pendingKeyFrames = new Set();
  return {
    reset(): void {
      pendingPackets.splice(0);
      pendingKeyFrames.clear();
      lastFrameNumber = null;
      lastDtsPushed = null;
      logger.info('TestVideoDecoder: reset()');
    },
    close(): void {
      logger.info('TestVideoDecoder: close()');
    },
    async push(packet: VideoPacket): Promise<void> {
      logger.debug(`push(): pts=${formatFractionAsExpression(packet.pts)}`);
      pendingPackets.push(packet);
      if (emitDecodedFramesInPresentationOrder) {
        pendingPackets.sort((a, b) => compareFractions(a.pts, b.pts));
      }
      if (packet.keyFrame) {
        pendingKeyFrames.add(approximateFractionValue(packet.dts));
      }
      if (lastDtsPushed !== null) {
        if (compareFractions(lastDtsPushed, packet.dts) >= 0) {
          throw new Error('Non-monotonically increasing timestamps passed to Test Video Decoder');
        }
      }
      lastDtsPushed = packet.dts;

      if (pendingPackets.length > 10) {
        // If the coordinator works correctly we should never get here
        throw new Error('TestVideoDecoder.push: EAGAIN');
      }
      logger.debug(`push(): pendingPackets=${pendingPackets.length}`);
    },
    async poll(): Promise<DecodedVideoFrame | null> {
      logger.debug('poll()');
      // Let's emulate some decoder delay
      if (pendingPackets.length < 3 && !flushed) {
        logger.debug(`poll(): emulating packet decoder delay. pendingPackets=${pendingPackets.length}`);
        return null;
      }

      const [packet] = pendingPackets.splice(0, 1);
      if (packet === undefined) {
        logger.debug('poll(): no pending packets');
        return null;
      }

      const testPacket : TestPacket = decodeTestPacket(packet.payload);
      if (testPacket.header.type !== 'video') {
        throw new Error('TestVideoDecoder.poll: Invalid packet');
      }

      const frameRate = testPacket.header.frameRate;

      const frameNumber = Math.round(approximateFractionValue(multiplyFractions(packet.pts, frameRate)));

      if (lastFrameNumber === null) {
        if (!testPacket.header.keyFrame) {
          throw new Error('TestVideoDecoder.poll: P-frame passed to video decoder with passing in I-frame first');
        }
        lastFrameNumber = frameNumber;
      }

      lastFrameNumber = frameNumber;

      const frameNumberAsFraction = { numerator: frameNumber, denominator: 1 };

      logger.debug('poll(): returning frame');
      return {
        pts: divideFractions(frameNumberAsFraction, frameRate),
        keyFrame: pendingKeyFrames.delete(approximateFractionValue(packet.pts)),
        info: {
          resolution: testPacket.header.resolution,
          pixelFormat: testPacket.header.pixelFormat,
          fieldOrder: testPacket.header.fieldOrder,
        },
        buffer: bufferCache.allocateBuffer(
          testPacket.payload.length,
          (buffer) => { buffer.set(testPacket.payload); }
        )
      };
    },
    async flush(): Promise<void> {
      flushed = true;
    },
  };
}
